import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaEdit, FaBars, FaTimes } from 'react-icons/fa';
import { useAuth, hasModuleAccess } from '../utils/hooks/useAuth';

const defaultMainItems = [
  { path: '/', label: 'Overview' },
  { path: '/crm/overview', label: 'iURM (CRM / Sales)' },
  { path: '/projects', label: 'iPROJECTS (Projects)' },
  { path: '/inventory', label: 'iMIM (Inventory / Ops)' },
  { path: '/finance', label: 'iFIM (Finance / Billing)' },
  { path: '/hr', label: 'iHIM (HR / People)' },
  { path: '/reports', label: 'Reports & Autonomy' }
];

const permissionMap = {
  "/":'overview',
  "/crm": "iurm",
  "/projects": "iproject",
  "/inventory": "imim",
  "/finance": "ifim",
  "/hr": "ihim",
  "/reports": "reportsAndAutonomy",
};

const defaultAiItems = [
  { path: '/marketplace', label: 'AI Agent Marketplace' }
];

export default function Sidebar() {
  const loc = useLocation();
  const { access, loading ,isOwner,logo} = useAuth(); // Use the shared hook instead of local state
  
  const [mainItems, setMainItems] = useState(() => {
    const saved = localStorage.getItem('sidebar_main_items');
    return saved ? JSON.parse(saved) : defaultMainItems;
  });

  const [aiItems, setAiItems] = useState(() => {
    const saved = localStorage.getItem('sidebar_ai_items');
    return saved ? JSON.parse(saved) : defaultAiItems;
  });

  const [editingPath, setEditingPath] = useState(null);
  const [editValue, setEditValue] = useState('');

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [loc.pathname]);

  const handleSave = (path, isMain) => {
    if (isMain) {
      const newItems = mainItems.map(item => item.path === path ? { ...item, label: editValue } : item);
      setMainItems(newItems);
      localStorage.setItem('sidebar_main_items', JSON.stringify(newItems));
    } else {
      const newItems = aiItems.map(item => item.path === path ? { ...item, label: editValue } : item);
      setAiItems(newItems);
      localStorage.setItem('sidebar_ai_items', JSON.stringify(newItems));
    }
    setEditingPath(null);
    window.dispatchEvent(new Event('sidebar_updated'));
  };

  const filteredMainItems = React.useMemo(() => {
    return mainItems.filter((item) => {
      // Always show dashboard
      // if (item.path === "/") return true;

      const key = permissionMap[item.path];
      
      if (!key) return true;

      return hasModuleAccess(key, access);
    });
  }, [mainItems, access]);
const singleModule = filteredMainItems.length <= 1;
  const renderItem = (i, isMain) => {
    const isCrmRoute = i.path.startsWith('/crm') && loc.pathname.startsWith('/crm');
    const isActive = isCrmRoute || loc.pathname === i.path || (i.path !== '/' && loc.pathname.startsWith(i.path) && !i.path.startsWith('/crm'));
    const isEditing = editingPath === i.path;

    return (
      <Link 
        key={i.path} 
        to={i.path} 
        style={{ textDecoration: 'none', color: 'inherit' }} 
        onClick={(e) => { 
          if (isEditing) e.preventDefault(); 
        }}
      >
        <div 
          className={`nav-item ${isActive ? 'active' : ''}`} 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            paddingRight: '15px',
            opacity: loading ? 0.6 : 1,
            pointerEvents: loading ? 'none' : 'auto'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: '10px' }}>
            <div className="nav-dot"></div>
            {isEditing ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => handleSave(i.path, isMain)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave(i.path, isMain);
                }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                autoFocus
                style={{ 
                  background: 'rgba(255, 255, 255, 0.1)', 
                  border: '1px solid rgba(255, 255, 255, 0.3)', 
                  color: 'inherit', 
                  padding: '2px 6px', 
                  borderRadius: '4px', 
                  width: '90%', 
                  outline: 'none' 
                }}
              />
            ) : (
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {i.label}
              </span>
            )}
          </div>
          {!isEditing && isActive && (
            <FaEdit 
              style={{ 
                cursor: 'pointer', 
                opacity: 0.5, 
                fontSize: '0.85em', 
                flexShrink: 0 
              }} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditingPath(i.path);
                setEditValue(i.label);
              }} 
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
            />
          )}
        </div>
      </Link>
    );
  };

  const MobileToggle = (
    <button
      className="sidebar-mobile-toggle"
      aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
      onClick={() => setIsMobileOpen(prev => !prev)}
    >
      {isMobileOpen ? <FaTimes /> : <FaBars />}
    </button>
  );

  const MobileStyles = (
    <style>{`
      .sidebar-mobile-toggle {
        display: none;
        position: fixed;
        top: 14px;
        left: 14px;
        z-index: 1100;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        border: 1px solid rgba(255,255,255,0.2);
        background: rgba(0,0,0,0.6);
        color: #fff;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        cursor: pointer;
      }

      .sidebar-mobile-overlay {
        display: none;
      }

      @media (max-width: 768px) {
        .sidebar-mobile-toggle {
          display: flex;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 1050;
          transform: translateX(-100%);
          transition: transform 0.25s ease-in-out;
        }

        .sidebar.mobile-open {
          transform: translateX(0);
        }

        .sidebar-mobile-overlay.mobile-open {
          display: block;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 1040;
        }
      }
    `}</style>
  );

  // Show loading state
  if (loading) {
    return (
      <>
        {MobileStyles}
        {MobileToggle}
        <div className={`sidebar-mobile-overlay ${isMobileOpen ? 'mobile-open' : ''}`} onClick={() => setIsMobileOpen(false)} />
        <div className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
          <div className="logo">
            <img style={{ width: "100%" }} src={logo?logo:'/logo512.png'} alt='logo' />
          </div>
          <div className="nav-section-title">Main</div>
          {/* Show skeleton items while loading */}
          {defaultMainItems.map((item, index) => (
            <div key={index} className="nav-item" style={{ opacity: 0.4, padding: '10px 15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="nav-dot"></div>
                <span>Loading...</span>
              </div>
            </div>
          ))}
          <div className="nav-section-title">AI</div>
          {defaultAiItems.map((item, index) => (
            <div key={index} className="nav-item" style={{ opacity: 0.4, padding: '10px 15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="nav-dot"></div>
                <span>Loading...</span>
              </div>
            </div>
          ))}
          {/* <div className="sidebar-bottom">Tenant: College Pod</div> */}
        </div>
      </>
    );
  }

  return (
    <>
      {MobileStyles}
      {singleModule?'':MobileToggle}
      <div className={`sidebar-mobile-overlay ${isMobileOpen ? 'mobile-open' : ''}`} onClick={() => setIsMobileOpen(false)} />
      <div className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="logo">
          <img style={{ width: "100%" }} src={logo?logo:'/logo512.png'} alt='logo' />
        </div>
        <div className="nav-section-title">Main</div>
        {filteredMainItems.map(i => renderItem(i, true))}
        {/* <div className="nav-section-title">AI</div> */}
        {/* {aiItems.map(i => renderItem(i, false))}
        <div className="sidebar-bottom">Tenant: College Pod</div> */}
        {
          isOwner&&(
            <a 
             style={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    border: '1px solid rgba(255, 255, 255, 0.3)', 
                    color: 'inherit', 
                    padding: '2px 6px', 
                    borderRadius: '4px', 
                    width: '90%', 
                    outline: 'none',
                    textAlign:"center",
                    textDecoration:"none"

                  }}
            target='_blanck' 
            href={`${process.env.REACT_APP_PARENT_BASE_URL}/ecosystem/${access?.tenantId}/overview`}>
               Go to ecosystem</a>
          )
        }
      </div>
    </>
  );
}